import { Rant, RantCategory } from '../types'
import { v4 as uuidv4 } from 'uuid'
// Generate a unique ID
export const generateId = (): string => {
  return uuidv4()
}
// Auto-categorize rant based on keywords
export const categorizeRant = (text: string): RantCategory => {
  const lowerText = text.toLowerCase()
  if (/bathroom|toilet|accident|stall|flush|public|restroom/.test(lowerText)) {
    return 'bathroom'
  }
  if (
    /food|eat|restaurant|diet|meal|hungry|starving|dinner|lunch|breakfast|snack/.test(
      lowerText,
    )
  ) {
    return 'food'
  }
  if (
    /medication|medicine|drug|pill|side effects|insurance|prescription|doctor|treatment|infusion/.test(
      lowerText,
    )
  ) {
    return 'medication'
  }
  if (
    /yoga|try|have you|just|exercise|relax|stress|anxiety|meditation|natural|cure|remedy/.test(
      lowerText,
    )
  ) {
    return 'yoga'
  }
  if (
    /emergency|urgent|panic|accident|suddenly|quick|fast|rush|hurry/.test(
      lowerText,
    )
  ) {
    return 'emergency'
  }
  if (
    /healthy|normal|people|friend|coworker|family|understand|get it|comprehend/.test(
      lowerText,
    )
  ) {
    return 'healthy-people'
  }
  if (
    /healthcare|system|insurance|bill|cost|expensive|coverage|denied|copay|deductible/.test(
      lowerText,
    )
  ) {
    return 'healthcare'
  }
  return 'all'
}
// Get category emoji and label
export const getCategoryInfo = (
  category: RantCategory,
): { emoji: string; label: string } => {
  switch (category) {
    case 'bathroom':
      return { emoji: '🚽', label: 'Bathroom Betrayals' }
    case 'food':
      return { emoji: '🍕', label: 'Food FOMO' }
    case 'medication':
      return { emoji: '💊', label: 'Medication Madness' }
    case 'yoga':
      return { emoji: '😤', label: 'Have You Tried Yoga?' }
    case 'emergency':
      return { emoji: '🏃‍♂️', label: 'Emergency Situations' }
    case 'healthy-people':
      return { emoji: '🤦', label: "Things Healthy People Don't Get" }
    case 'healthcare':
      return { emoji: '💸', label: 'Healthcare System BS' }
    default:
      return { emoji: '🔥', label: 'All Rants' }
  }
}
// Get random validation message after rant submission
export const getRandomValidation = (): string => {
  const validations = [
    'That IS bullshit. Noted.',
    'Preaching to the choir.',
    "Valid rage. You're not alone.",
    "If IBD was a person, we'd all have words.",
    'Heard, validated, and added to the pile of shit.',
    'Your IBD rage has been documented. Thank you for your service.',
    "Damn right. We're nodding in agreement.",
    'Your frustration is our frustration.',
    'Added to the official IBD grievances list.',
    "This is why we can't have nice things. And by things, we mean intestines that work.",
  ]
  return validations[Math.floor(Math.random() * validations.length)]
}
// Get random footer solidarity message
export const getRandomFooterMessage = (): string => {
  const messages = [
    'Sometimes you just gotta scream into the void. This is that void.',
    'Your anger is valid. Your pain is real. Your rants are welcome.',
    'Tracking your rage is just as valid as tracking your symptoms.',
    'Built by someone who gets it, for people who live it.',
    'Where IBD frustrations come to party.',
    'Because sometimes you need to vent to people who actually understand.',
    'No toxic positivity here. Just real talk.',
    "Where 'Have you tried...' comments come to die.",
    'Because therapy is yet another doctor appointment.',
    "IBD: Where 'I'll be right back' is both a promise and a prayer.",
  ]
  return messages[Math.floor(Math.random() * messages.length)]
}
// Get placeholder text for rant input
export const getRandomPlaceholder = (): string => {
  const placeholders = [
    "I'm so sick of...",
    'Why does everyone think...',
    'I swear if one more person tells me to try eating saurkraut...',
    'Can we talk about how...',
    'Nobody warns you about...',
    'The worst part about IBD is...',
    'I try to make IBD sexy but...',
    "To the person who said 'it's just a stomach ache'...",
    'If I had a dollar for every time someone suggested probiotics...',
    'Oh joy another call with insurance...',
    'Dear person who designed this public bathroom stall door gap...',
    'My colon has entered the chat and it has OPINIONS about...',
    "Hot take: 'Have you tried eliminating gluten?' is not helpful when...",
    'Today in IBD adventures: I was minding my own business when suddenly...',
    'Shoutout to the person who invented single-ply toilet paper. I just want to talk...',
    'Can someone explain why my body decided that [insert food] is now the enemy?',
    'My relationship with food is complicated. Today it got even weirder when...',
  ]
  return placeholders[Math.floor(Math.random() * placeholders.length)]
}
// Get encouragement message based on character count
export const getEncouragementMessage = (charCount: number): string => {
  if (charCount === 0) {
    return 'Tell us what pisses you off...'
  } else if (charCount < 50) {
    return 'Keep going...'
  } else if (charCount < 150) {
    return "That's it, let it out"
  } else if (charCount < 300) {
    return "Don't hold back"
  } else {
    return 'YES. Tell us everything.'
  }
}
// Save rant to localStorage
export const saveRant = (rant: Rant): void => {
  const rants = getRants()
  rants.push(rant)
  localStorage.setItem('ibdRants', JSON.stringify(rants))
}
// Get all rants from localStorage
export const getRants = (): Rant[] => {
  const rantsJson = localStorage.getItem('ibdRants')
  if (!rantsJson) return []
  return JSON.parse(rantsJson)
}
// Update rant in localStorage
export const updateRant = (updatedRant: Rant): void => {
  const rants = getRants()
  const index = rants.findIndex((rant) => rant.id === updatedRant.id)
  if (index !== -1) {
    rants[index] = updatedRant
    localStorage.setItem('ibdRants', JSON.stringify(rants))
  }
}
// Format timestamp to relative time
export const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now()
  const diffInSeconds = Math.floor((now - timestamp) / 1000)
  if (diffInSeconds < 60) {
    return 'just now'
  }
  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`
  }
  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
  }
  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
  }
  const diffInWeeks = Math.floor(diffInDays / 7)
  return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`
}
// Get random IBD joke or meme
export const getRandomIBDJoke = (): string => {
  const jokes = [
    'My superpower? I can rate any public bathroom in under 30 seconds.',
    'IBD: The only club where the initiation is literal shit.',
    "Me: 'I need to use the bathroom'\nMy intestines: 'You have 20 seconds'",
    'Normal people: Track their steps\nMe: Track my bathroom visits',
    'Plot twist: I spent my vacation on the toilet.',
    "Running late?! No. I'm just running.'",
    "I don't always plan my day, but when I do, it's around bathroom locations.",
    "My dating profile: Swipe right if you're cool with sudden detours.",
    'IBD: Because life was too predictable.',
    "I'm not antisocial, I'm just strategically positioned near bathrooms.",
    'My Google Maps is 90% bathroom reviews.',
    "I've seen more bathrooms than a plumber and I'm not even getting paid.",
    'IBD: The gift that keeps on giving. And giving. And giving.',
    "Roses are red, violets are blue, I'm in the bathroom, how about you?",
    'My life is like a GPS: Recalculating route to nearest bathroom.',
    "I don't need a gym membership, I get my cardio sprinting to bathrooms.",
    "IBD is my body's way of keeping me humble.",
    'Plot twist: I AM the party pooper.',
    "My autobiography: 'Where's the Bathroom?: A Journey'",
    "I put the 'irritable' in irritable bowel disease.",
    'IBD: Making every meal a game of Russian roulette since forever.',
    'My intestines have more drama than a reality TV show.',
    "I'm basically a professional bathroom critic at this point.",
    'IBD turned me into a bathroom cartographer.',
    "My body's check engine light is permanently on.",
    'I have a PhD in Public Restroom Geography.',
    "IBD: Where 'grab and go' takes on a whole new meaning.",
    'My social battery runs on bathroom availability.',
    'I collect bathroom horror stories like Pokémon cards.',
    "IBD: The body's way of saying 'surprise!' at the worst times.",
  ]
  return jokes[Math.floor(Math.random() * jokes.length)]
}
